/* eslint-disable @typescript-eslint/naming-convention */

import { User } from '@app/interfaces/user'
import { Localizable } from '@app/components/generic/generic'
import type { ArchiveDocumentActivity, CountryListItem, Partner, State, Transition } from '@app/generated/api/model'

import { BALZACBook } from './balzac-books'
import { Department } from './department'
import { Landlord } from './landlord'
import { Place } from './place'
import { Supplier } from './supplier'

export interface ArchiveDocumentSplitPayload {
  newName: string
  splitAfterPageNumber: number
}

export interface ArchiveDocumentField {
  name: string
  label_en: string
  label_fr: string
  type: string
  options?: string
  required?: boolean
  recommended?: boolean
  tooltip_en?: string
  tooltip_fr?: string
}

export type ArchiveDocumentHintValue = string | number | boolean | null | ArchiveDocumentHintValue[] | { [fieldName: string]: ArchiveDocumentHintValue }

export interface ArchiveDocumentHints {
  type?: string
  [fieldName: string]: ArchiveDocumentHintValue | undefined
}

export interface ArchiveDocumentType extends Localizable {
  id?: number
  department?: Department | null
  code: string
  priority?: boolean
  batch?: boolean
  archive_document_fields?: ArchiveDocumentField[]
}

export interface ArchiveDocumentForm {
  [fieldName: string]: unknown
  filename: string
  uuid: string
  type?: ArchiveDocumentType | null
  state: { -readonly [key in keyof State]: State[key] }
  workflow: string
  transitions: Transition[]
  is_modifiable: boolean
  hints: ArchiveDocumentHints
  voucher_code?: string
  // specifying null as a possibility here is important to allow clearing the value, i.e. deleting an existing country / place association
  country?: Pick<CountryListItem, 'id' | 'code_iso' | 'code_iso_2' | 'label_en' | 'label_fr'> | null
  place?: Place | null
  project_codes?: string[] | null
  activity?: ArchiveDocumentActivity | null
  department?: Department
  date?: string
  balzac_book?: BALZACBook
  supplier?: Supplier
  landlord?: Landlord
  partner?: Partner
  third_party_code?: string
  budget_lines?: string[]
  asset_categories?: string[]
  program_place?: Place | null
  pmf_activity_number?: string | null
  created_by?: User
  created_at?: string
  // written at runtime through TypeGenericComponent.setDoc(), and accepted by the API, but never
  // declared here until acted-core#231 needed to read them type-safely to build the save payload
  asset_code?: string | null
  warehouse_code?: string | null
  document_number?: string | null
  item_category?: string | null
  order_form_date?: string | null
  staff_name?: string | null
  homere_code?: string | null
}

const stateNew: State = {
  name: 'new',
  label_en: 'new',
  label_fr: 'new',
  severity: 'info',
}

// only used for tests
export function newArchiveDocument (): ArchiveDocumentForm {
  return {
    filename: '',
    uuid: '',
    type: { id: 0, code: 'TYPE_UNKNOWN', label_en: 'Unknown', label_fr: 'Inconnu' },
    state: structuredClone(stateNew),
    workflow: 'workflow',
    hints: {},
    transitions: [{
      name: 'validate', label_en: 'validate', label_fr: 'validate',
    }],
    is_modifiable: true,
  }
}
